import React, { useState, useCallback, useEffect, useRef } from 'react'
import { useSelector } from 'hooks/redux'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { DRAWER_SELECT_MODE } from 'constants/ui/ptoDiagram'
import strings from 'utils/strings'
import { getDomainsList } from 'selectors/powerTakeOff'
import { createPtoTemplate } from 'actions/powerTakeOff'
import { Row, Col, Alert, Header } from 'components/patterns'

import {
  ModalSmall,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalContent,
  ModalMainContainer,
  ModalCloseButton,
  ModalButton,
} from 'components/patterns/Modal'
import { Icon__Alert as IconAlert } from 'components/patterns/Icon/components'
import PtoTemplateDiffTable from './PtoTemplateDiffTable'
import PtoTemplateNotes from './PtoTemplateNotes'
import PtoCreateTemplateActionButtons from './PtoCreateTemplateActionButtons'
import {
  ViewWrapper,
  AlertContainer,
  CreateTemplateWrapper,
  CreateTemplateFooter,
  BottomContainer,
  ModalList,
} from './CreateTemplate.style'

const CreateTemplate = ({
  toggleTemplateDrawer,
  isDrawerVisible,
  setDrawerState,
  setDrawerTitle,
}) => {
  const viewWrapperScrollerRef = useRef()
  const dispatch = useDispatch()
  const domainsList = useSelector(getDomainsList)

  const [isPtoDiffView, setPtoDiffView] = useState(true)
  const [isNotesView, setNotesView] = useState(false)

  const [notes, setNotes] = useState('')
  const [templateName, setTemplateName] = useState('')
  const [templateDescription, setTemplateDescription] = useState('')
  const [selectedDomains, setSelectedDomains] = useState({})

  const [submitting, setSubmitting] = useState(false)
  const [isGlobalEnabled, setIsGlobalEnabled] = useState(false)

  const [showSaveModal, setShowSaveModal] = useState(false)

  const [useRoundOffSettings, setUseRoundOffSetings] = useState()

  const [deleteInvalidSettings, setDeleteInvalidSettings] = useState()

  const handleDrawerAndClearTemplate = useCallback(() => {
    setDrawerState(DRAWER_SELECT_MODE)
    setNotesView(false)
    setPtoDiffView(false)
  }, [setNotesView, setDrawerState])

  const handleCancelToTemplateView = useCallback(() => {
    setDrawerState(DRAWER_SELECT_MODE)
    setNotesView(false)
    setPtoDiffView(false)
    setUseRoundOffSetings()
    setDeleteInvalidSettings()
  }, [
    setDrawerState,
    setNotesView,
    setPtoDiffView,
    setUseRoundOffSetings,
    setDeleteInvalidSettings,
  ])

  const handleSetToNotesView = useCallback(
    (_, skipInvalidCheck = false) => {
      const isHavingInvalidSettingsDomains = Object.keys(selectedDomains).some(
        (item) =>
          domainsList.find(
            (domain) =>
              selectedDomains[item] &&
              domain.id === item &&
              domain.domainHasOutofRangeSettings
          )
      )
      if (!skipInvalidCheck && isHavingInvalidSettingsDomains) {
        setShowSaveModal(true)
      } else {
        setNotesView(true)
        setPtoDiffView(false)
        setShowSaveModal(false)
      }
    },
    [
      setNotesView,
      setPtoDiffView,
      setShowSaveModal,
      selectedDomains,
      domainsList,
    ]
  )

  const handleCancelToDiffView = useCallback(() => {
    setNotesView(false)
    setPtoDiffView(!isPtoDiffView)
    setUseRoundOffSetings()
    setDeleteInvalidSettings()
  }, [
    setNotesView,
    setPtoDiffView,
    isPtoDiffView,
    setUseRoundOffSetings,
    setDeleteInvalidSettings,
  ])

  const handleNotesChange = useCallback(
    (notesVal) => {
      setNotes(notesVal)
    },
    [setNotes]
  )

  useEffect(() => {
    setDrawerTitle(strings.new_pto_template_drawer_title)
  }, [setDrawerTitle])

  useEffect(() => {
    const tempSelectedDomains = domainsList.reduce(
      (acc, domain) => ({ ...acc, [domain.id]: true }),
      {}
    )

    setSelectedDomains(tempSelectedDomains)
  }, [domainsList, setSelectedDomains])

  useEffect(() => {
    if (viewWrapperScrollerRef.current && (isNotesView || isPtoDiffView)) {
      viewWrapperScrollerRef.current.scrollTop = 0
    }
  }, [isNotesView, isPtoDiffView])

  const handleCreateWithNotes = useCallback(() => {
    const selectedDomainArray = Object.keys(selectedDomains).reduce(
      (acc, key) => {
        if (selectedDomains[key]) {
          return [...acc, key]
        }
        return acc
      },
      []
    )

    setSubmitting(!submitting)
    dispatch(
      createPtoTemplate({
        notes,
        templateName,
        templateDescription,
        domainList: selectedDomainArray,
        isTemplateRounded: deleteInvalidSettings ? false : useRoundOffSettings,
        callback: () => {
          toggleTemplateDrawer(!isDrawerVisible)
          setSubmitting(false)
        },
      })
    )

    setNotes('')
  }, [
    submitting,
    deleteInvalidSettings,
    dispatch,
    notes,
    templateName,
    templateDescription,
    selectedDomains,
    toggleTemplateDrawer,
    isDrawerVisible,
    useRoundOffSettings,
  ])

  const handleUseRoundedSettingsClick = () => {
    setUseRoundOffSetings(true)
    handleSetToNotesView(null, true)
  }

  const handleDeleteInvalidSettingsClick = () => {
    setDeleteInvalidSettings(true)
    handleSetToNotesView(null, true)
  }

  return (
    <>
      <ViewWrapper ref={viewWrapperScrollerRef}>
        <AlertContainer>
          <Alert
            type="info"
            message={strings.create_template_alert_text}
            persistent
          />
        </AlertContainer>
        <CreateTemplateWrapper>
          {isPtoDiffView && (
            <BottomContainer>
              <PtoTemplateDiffTable
                setIsGlobalEnabled={setIsGlobalEnabled}
                templateName={templateName}
                setTemplateName={setTemplateName}
                templateDescription={templateDescription}
                setTemplateDescription={setTemplateDescription}
                selectedDomains={selectedDomains}
                setSelectedDomains={setSelectedDomains}
              />
            </BottomContainer>
          )}

          {isNotesView && (
            <BottomContainer>
              <PtoTemplateNotes
                handleNotesChange={handleNotesChange}
                selectedDomains={selectedDomains}
                useRoundOffSettings={useRoundOffSettings}
                deleteInvalidSettings={deleteInvalidSettings}
              />
            </BottomContainer>
          )}
        </CreateTemplateWrapper>
        <Row align="bottom">
          <Col span={24}>
            <CreateTemplateFooter>
              <PtoCreateTemplateActionButtons
                isPtoDiffView={isPtoDiffView}
                isNotesView={isNotesView}
                isGlobalEnabled={isGlobalEnabled}
                notes={notes}
                submitting={submitting}
                selectedDomains={selectedDomains}
                handleNotesChange={handleNotesChange}
                handleDrawerAndClearTemplate={handleDrawerAndClearTemplate}
                handleSetToNotesView={handleSetToNotesView}
                handleCancelToTemplateView={handleCancelToTemplateView}
                handleCancelToDiffView={handleCancelToDiffView}
                handleCreateWithNotes={handleCreateWithNotes}
              />
            </CreateTemplateFooter>
          </Col>
        </Row>
      </ViewWrapper>
      {showSaveModal && (
        <ModalSmall data-testid="pto-values-out-of-range-modal">
          <ModalHeader>
            <Header type="h3">
              <IconAlert />
              {strings.pto_template_values_out_of_range_header}
            </Header>
            <ModalCloseButton onClick={() => setShowSaveModal(false)} />
          </ModalHeader>
          <ModalBody>
            <ModalMainContainer>
              <ModalContent>
                {strings.pto_save_template_content_pre}
                <ModalList>
                  {strings.pto_save_template_content_list.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ModalList>
              </ModalContent>
            </ModalMainContainer>
          </ModalBody>
          <ModalFooter data-testid="modal-footer">
            <ModalButton
              type="secondary"
              onClick={handleDeleteInvalidSettingsClick}
            >
              {strings.pto_del_invalid_settings}
            </ModalButton>
            <ModalButton type="primary" onClick={handleUseRoundedSettingsClick}>
              {strings.pto_use_rounded_settings}
            </ModalButton>
          </ModalFooter>
        </ModalSmall>
      )}
    </>
  )
}

CreateTemplate.propTypes = {
  serialNumber: PropTypes.string,
  toggleTemplateDrawer: PropTypes.func,
  isDrawerVisible: PropTypes.bool,
  templateType: PropTypes.string,
  setDrawerTitle: PropTypes.func,
  setDrawerState: PropTypes.func,
}

export default CreateTemplate
